import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconView, IconInfo } from '../../icons';
import { useAuth } from '../../context/AuthContext';

/**
 * M-UX-7.1 (`plan-ux-7.1-advisor-shell.md` §3) — persistente Amber-Kontext-
 * Leiste für den Advisor-Lesemodus ("DATEV-2030"-Shell).
 *
 * Self-contained: liest ihren gesamten Zustand über {@link useAuth} — kein
 * Prop-Drilling von `MainLayout`. Spiegelt bewusst das Datenzugriffs- und
 * Exit-Muster von `components/tenant/TenantSwitcher.tsx` (Ein-Quelle-Prinzip,
 * SSOT = Auth-State). Die eigentliche Sichtbarkeits-Steuerung (Flag +
 * Advisor-Modus) liegt beim Render-Guard in `MainLayout`; diese Komponente
 * ist zusätzlich defensiv gegen Fehlbedienung (rendert `null` außerhalb des
 * Advisor-Kontexts).
 *
 * W7 Design-Polish (europäisches Marktführer-Niveau):
 * - **Sticky-Einheit:** Die Leiste haftet NICHT selbst — `MainLayout` fasst
 *   `<header>` + diese Leiste in einen gemeinsamen `sticky top-0 z-50`-Wrapper,
 *   sodass beide beim Scrollen als Einheit oben kleben (kein `top`-Offset).
 * - **Motion:** dezentes Einfahren beim Moduswechsel via `.advisor-context-bar`-
 *   Keyframe (`styles/tokens.css`), `prefers-reduced-motion`-fest.
 * - **Legal-Popover a11y:** Fokus wandert beim Öffnen in den Dialog, Tab ist
 *   gefangen (Text-only ⇒ Fokus bleibt am Container), Escape schließt und gibt
 *   den Fokus deterministisch an den Trigger zurück, Klick-außerhalb schließt
 *   (mit Fokus-Rückgabe nur bei nicht-fokussierbarem Ziel), `aria-expanded`/
 *   `aria-controls`/`aria-haspopup`/`role="dialog"` gesetzt. Bewusst KEIN
 *   `common/Modal` (das ist ein Vollbild-Backdrop-Overlay — für einen kleinen,
 *   an einen Trigger verankerten Info-Hinweis unangemessen); die a11y-Technik
 *   (Fokus-Trap, Fokus-Rückgabe) folgt aber demselben bewährten Muster.
 *
 * M-UX-7.2-Nachzug (Plan §3.2/§3.3): Der „Mandant wechseln"-Button
 * (→ `/dashboard/mandanten`) ist jetzt aktiv — die Zielroute ist mit M-UX-7.2
 * registriert (kein transientes 404-Fenster mehr). Er navigiert zur
 * Mandanten-Übersicht; der bestehende `TenantSwitcher` in der Top-Bar bleibt
 * als zweiter Wechsel-Pfad erhalten.
 */
export interface AdvisorContextBarProps {
    /** Optionaler Layout-Hook für den Aufrufer; keine weiteren Pflicht-Props. */
    className?: string;
}

/** Fokussierbare Elemente (identisch zum `common/Modal`-Muster). */
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const AdvisorContextBar: React.FC<AdvisorContextBarProps> = ({ className }) => {
    const { t } = useTranslation('common');
    const { availableTenants, activeTenantId, activeRole, switchTenant } = useAuth();
    const navigate = useNavigate();

    const [legalOpen, setLegalOpen] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const infoButtonRef = useRef<HTMLButtonElement>(null);
    // Stabile ID für die aria-controls-Verknüpfung Trigger ⇄ Popover.
    const popoverId = useId();

    useEffect(() => {
        if (!legalOpen) return undefined;

        const triggerEl = infoButtonRef.current;

        // Fokus in den Dialog bewegen, damit Tastatur-/Screenreader-Nutzer auf
        // dem Hinweis landen und der Trap einen Anker hat. Text-only Popover ⇒
        // der Container selbst ist fokussierbar (`tabIndex={-1}`).
        popoverRef.current?.focus();

        function handlePointerDown(event: MouseEvent) {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (popoverRef.current?.contains(target) || infoButtonRef.current?.contains(target)) {
                return;
            }
            setLegalOpen(false);
            // Landet der Klick auf nicht-fokussierbarer Fläche, würde der Fokus
            // mit dem entfernten Popover verloren gehen → zurück zum Trigger.
            // Auf einem echten Control lassen wir diesem den gerade erhaltenen
            // Fokus.
            if (!target.closest(FOCUSABLE_SELECTOR)) {
                triggerEl?.focus();
            }
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                event.stopPropagation();
                setLegalOpen(false);
                triggerEl?.focus();
                return;
            }
            if (event.key !== 'Tab' || !popoverRef.current) return;

            const focusable = Array.from(
                popoverRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
            ).filter((el) => el.offsetParent !== null);

            // Text-only Dialog: Fokus am Container halten (kein Ausbrechen).
            if (focusable.length === 0) {
                event.preventDefault();
                popoverRef.current.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [legalOpen]);

    // Defensive: außerhalb des Advisor-Kontexts (Fehlbedienung/Direktimport)
    // rendert die Leiste nichts. Die eigentliche Sichtbarkeits-Entscheidung
    // (Flag + Advisor-Modus) trifft MainLayout beim Einhängen (§5).
    if (activeRole !== 'advisor') {
        return null;
    }

    const activeTenant = availableTenants.find((tenant) => tenant.tenantId === activeTenantId);
    const mandantName = activeTenant?.displayName ?? t('tenant.switchLabel');
    const ownTenant = availableTenants.find((tenant) => tenant.role === 'owner');

    const handleBackToOwn = () => {
        if (!ownTenant) return;
        switchTenant(ownTenant.tenantId);
        navigate('/dashboard');
    };

    // M-UX-7.2-Nachzug: zur Mandanten-Übersicht wechseln (Route ab 7.2
    // registriert). Kein Tenant-Wechsel hier — die Auswahl trifft der Nutzer
    // auf der Übersichtsseite.
    const handleSwitchClient = () => {
        navigate('/dashboard/mandanten');
    };

    const handleToggleLegal = () => {
        setLegalOpen((open) => !open);
    };

    return (
        <div
            role="region"
            aria-label={t('advisorBar.ariaRegion')}
            className={`advisor-context-bar relative flex items-center gap-3 px-4 py-2 text-sm${
                className ? ` ${className}` : ''
            }`}
        >
            <span className="advisor-context-glyph inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
                <IconView aria-hidden />
            </span>
            <span className="min-w-0 truncate font-medium">
                {t('advisorBar.viewing', { mandant: mandantName })}
            </span>

            <button
                ref={infoButtonRef}
                type="button"
                onClick={handleToggleLegal}
                aria-label={t('advisorBar.legalInfoAria')}
                aria-expanded={legalOpen}
                aria-haspopup="dialog"
                aria-controls={popoverId}
                className="advisor-context-control inline-flex shrink-0 items-center rounded-full p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--advisor-context-accent)]"
            >
                <IconInfo aria-hidden />
            </button>

            <div className="ml-auto flex shrink-0 items-center gap-2">
                <button
                    type="button"
                    onClick={handleSwitchClient}
                    className="rounded-md border border-[color:var(--advisor-context-border)] px-3 py-1.5 text-xs font-semibold text-[color:var(--advisor-context-fg)] transition hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--advisor-context-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--advisor-context-bg)]"
                >
                    {t('advisorBar.switchClient')}
                </button>
                {ownTenant && (
                    <button
                        type="button"
                        onClick={handleBackToOwn}
                        className="rounded-md bg-[#20D08A] px-3 py-1.5 text-xs font-semibold text-[#0C1322] transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20D08A] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--advisor-context-bg)]"
                    >
                        {t('advisorBar.backToOwn')}
                    </button>
                )}
            </div>

            {legalOpen && (
                <div
                    ref={popoverRef}
                    id={popoverId}
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('advisorBar.legalInfoAria')}
                    tabIndex={-1}
                    className="advisor-context-popover absolute left-0 top-full z-50 mt-2 w-80 max-w-[90vw] rounded-card border border-default bg-surface-raised p-3 text-xs leading-relaxed text-secondary shadow-[var(--shadow-overlay)] focus:outline-none"
                >
                    {t('tenant.acceptLegalNotice', { mandant: mandantName })}
                </div>
            )}
        </div>
    );
};

export default AdvisorContextBar;
